import { type ComponentType } from "react";

import { type SystemUserReportTypeEnum } from "@/providers/store/api/reports/system-user-report-type.enum";

/**
 * Simplified tab type - no more dividers, cleaner interface
 */
export interface Tab {
  id: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  closable: boolean;
  showPreviewIndicator?: boolean;
  reportKind?: "saved" | "custom";
  /** When reportKind is saved: API SystemUserReport.type (e.g. PROJECTION). Enables fresh fetch when reopening the tab. */
  savedSystemUserReportType?: SystemUserReportTypeEnum;
}

/**
 * Tab source: where did this tab come from?
 */
export enum TabSource {
  /** Current page - always first, non-closable */
  Page = "page",
  /** Opened report - closable */
  Report = "report",
}

/**
 * Internal tab with source tracking
 */
export interface InternalTab extends Tab {
  source: TabSource;
}

/**
 * Provider state - single source of truth
 */
export interface WorkspaceTabsState {
  tabs: InternalTab[];
  activeTabId: string | null;
  pageTabId: string | null;
  /** When true, SET_PAGE_TAB won't activate page tab (used when opening report from AI chat) */
  skipNextPageActivation: boolean;
}

/**
 * State actions
 */
export type WorkspaceTabsAction =
  | { type: "SET_PAGE_TAB"; tab: Tab }
  | { type: "SET_ACTIVE_TAB"; tabId: string }
  | { type: "OPEN_REPORT"; tab: Tab }
  | { type: "UPDATE_REPORT"; tabId: string; updates: Partial<Tab> }
  | { type: "CLOSE_REPORT"; tabId: string }
  | { type: "CLEAR_REPORTS" };
